import React, { useEffect, useMemo, useState } from "react";
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "context/authContext";
import LoadingScreen from "components/Loading";
import { getBranches, getMyTeam } from "services/api";
import BranchModel from "models/branchModel";
import MDBox from "components/MDBox";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  Grid,
  Icon,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { getJobs, getJobRequisitionsPageData } from "services/api";
import JobModel from "models/jobModel";
import { getDepartments } from "services/api";
import { getJobToBranch } from "services/api";
import { calculateAverageLocation } from "services/utils";
import { useTranslation } from "react-i18next";
import { ReactFlowProvider } from "reactflow";
import { hasEditAccess } from "services/utils";
import { buttonStyles } from "services/utils";
import {
  getAllJobRequisitions,
  getJobRequisitionsByEmployee,
  getApplicantDataByInterviewer,
} from "services/api";
import {
  Cancel,
  CheckCircle,
  Comment,
  Delete,
  Event,
  Search,
  Visibility,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import MDButton from "components/MDButton";
import AddTalentRequisitionDialog from "components/AddTalentRequisitionDialog";
import { saveJobRequisitions } from "services/api";
import { getEmployees } from "services/api";
import { deleteJobRequisitions } from "services/api";
import MeetingsPage from "layouts/admin/talentAcquisition/MeetingsPage";
import InterviewCalendar from "components/InterviewCalendar";
import MeetingsDialogWrapper from "components/MeetingsDialogWrapper";
import ConfirmationDialog from "components/ConfirmationDialog";

const TalentAcquisitionPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [meetingsLoading, setMeetingsLoading] = useState(false);
  const [branches, setBranches] = useState();
  const [departments, setDepartments] = useState();
  const [center, setCenter] = useState(null);
  const [jobRequisitions, setJobRequisitions] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [openCommentDialog, setOpenCommentDialog] = useState(false);
  const [comment, setComment] = useState("");
  const [commentData, setCommentData] = useState();
  const [adminOrCeo, setAdminOrCeo] = useState(false);
  const [openMeetingsDialog, setOpenMeetingsDialog] = useState(false);
  const [deleteRequest, setDeleteRequest] = useState();
  // auth context
  const {
    jobs,
    setJobs,
    setJobToBranch,
    jobToBranch,
    modules,
    employee,
    allEmps,
    setAllEmps,
    isTopManagement,
  } = useAuth();

  const handleOpenDialog = (req) => {
    setSelectedRequest(req); // null = add new, object = edit
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRequest(null);
  };

  const handleOpenMeetingsDialog = () => setOpenMeetingsDialog(true);
  const handleCloseMeetingsDialog = () => setOpenMeetingsDialog(false);

  const handleSaveRequest = async (data) => {
    const now = new Date();

    let request;
    //CEO
    if (employee.role === 1) {
      request = {
        ...data,
        requestedBy: employee.id,
        ceoApproved: true,
        ceoApprovedAt: now.toISOString(),
        hrApprovedBy: employee.id,
        hrApprovedAt: now.toISOString(),
        status: "APPROVED",
      };
    }
    //Director
    else if (isTopManagement === true) {
      request = {
        ...data,
        requestedBy: employee.id,
        directorApproved: true,
        directorApprovedAt: now.toISOString(),
        status: "PENDING_CEO_APPROVAL",
      };
    }
    //Admin/HR
    else if (employee.isAdmin === true) {
      request = {
        ...data,
        requestedBy: employee.id,
        hrApprovedBy: employee.id,
        hrApprovedAt: now.toISOString(),
        status: "PENDING_DIRECTOR_APPROVAL",
      };
    } else {
      request = {
        ...data,
        requestedBy: employee.id,
        status: "PENDING_HR_APPROVAL",
      };
    }

    const newReq = await saveJobRequisitions(request);
    const enrichedReq = {
      ...newReq,
      // 1. Job Title Logic
      jobTitle: newReq.isForCompany
        ? jobs?.find((j) => j.jobId === newReq.jobId)?.jobName || "" // Internal: Lookup ID
        : newReq.jobTitle || "", // External: Use text directly

      // 2. Department Name Logic
      departmentName: newReq.isForCompany
        ? departments?.find((d) => d?.departmentId === newReq?.departmentId)?.departmentName || ""
        : newReq.departmentName || "",

      // 3. Branch Name Logic
      branchName: newReq.isForCompany
        ? branches?.find((b) => b.branchId === newReq.branchId)?.branchName || ""
        : newReq.branchName || "",

      // 4. Requested By (Standard logic)
      requestedByName:
        newReq.requestedBy === null
          ? "HR"
          : allEmps?.find((e) => e.id === newReq.requestedBy)?.name || "Unknown",
    };

    setJobRequisitions((prev) => {
      if (data?.id) {
        // Update existing request in state
        return prev.map((r) => (r.id === data.id ? { ...r, ...newReq } : r));
      } else {
        // Add new request to state
        return [enrichedReq, ...prev];
      }
    });
    handleCloseDialog();
  };

  const handleApprove = async (data) => {
    const now = new Date();

    let request;
    //CEO
    if (employee.role === 1) {
      request = {
        ...data,
        ceoApproved: true,
        ceoApprovedAt: now.toISOString(),
        status: "APPROVED",
      };
    }

    //Director
    else if (isTopManagement === true) {
      request = {
        ...data,
        directorApproved: true,
        directorApprovedAt: now.toISOString(),
        status: "PENDING_HR_APPROVAL",
      };
    }

    //Admin/HR
    else if (employee.isAdmin === true) {
      request = {
        ...data,
        hrApprovedBy: employee.id,
        hrApprovedAt: now.toISOString(),
        status: "PENDING_CEO_APPROVAL",
      };
    } else {
      request = {
        ...data,
        status: "PENDING_DIRECTOR_APPROVAL",
      };
    }

    const newReq = await saveJobRequisitions(request);
    setJobRequisitions((prev) => {
      // Update existing request in state
      return prev.map((r) => (r.id === data.id ? { ...r, ...newReq } : r));
    });
  };

  const handleCommentSubmit = async () => {
    const request =
      employee.role == 1
        ? {
            ...commentData,
            ceoComment: comment,
          }
        : {
            ...commentData,
            hrComment: comment,
          };
    const newReq = await saveJobRequisitions(request);
    setJobRequisitions((prev) => {
      // Update existing request in state
      return prev.map((r) => (r.id === commentData.id ? { ...r, ...newReq } : r));
    });
    setOpenCommentDialog(false);
    setComment("");
    setCommentData();
  };

  const handleDeleteRequest = (requisition) => {
    // await deleteJobRequisitions(id);
    setDeleteRequest(requisition);
    setConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    const request = {
      ...deleteRequest,
      status: "REJECTED",
    };
    const newReq = await saveJobRequisitions(request);
    if (newReq != "error") {
      setJobRequisitions((prev) => {
        // Update existing request in state
        return prev.map((r) => (r.id === deleteRequest.id ? { ...r, ...newReq } : r));
      });
    }
    setDeleteRequest(null);
    setConfirmOpen(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Single API call to the new endpoint
        console.log("Employee Id::", employee.id);
        const pageData = await getJobRequisitionsPageData(employee.id);
        console.log("Page Data::", pageData);

        if (pageData !== "error") {
          // Populate states from pageData
          setJobs(pageData.jobs);
          setBranches(pageData.branches);
          setDepartments(pageData.departments);
          setJobToBranch(pageData.jobToBranches);
          setAllEmps(pageData.employees);

          // Decide which job requisitions to show
          if (employee?.isAdmin || employee?.role === 1) {
            setAdminOrCeo(true);
            setJobRequisitions(pageData.allJobRequisitions);
          } else {
            setJobRequisitions(pageData.jobRequisitionsByEmp);
          }

          // Calculate center from branches
          setCenter(calculateAverageLocation(pageData.branches));
        }
      } catch (error) {
        console.error("Error loading job requisitions page data:", error);
      } finally {
        setLoading(false);
      }

      // Set edit permissions
      if (employee.isAdmin || employee.role === 1) {
        setCanEdit(hasEditAccess(modules, "Talent Acquisition", employee));
      } else {
        setCanEdit(true);
      }
    };

    fetchData();
  }, [employee.id]);

  // ✅ Summary counts (you can keep these outside useMemo since they're simple)
  const pendingHrCount =
    jobRequisitions?.filter((j) => j.status === "PENDING_HR_APPROVAL").length || 0;
  const pendingDirectorCount =
    jobRequisitions?.filter((j) => j.status === "PENDING_DIRECTOR_APPROVAL").length || 0;
  const pendingCeoCount =
    jobRequisitions?.filter((j) => j.status === "PENDING_CEO_APPROVAL").length || 0;
  const approvedCount = jobRequisitions?.filter((j) => j.status === "APPROVED").length || 0;
  const rejectedCount = jobRequisitions?.filter((j) => j.status === "REJECTED").length || 0;
  const completedCount = jobRequisitions?.filter((j) => j.status === "COMPLETED").length || 0;

  const { filteredRequisitions, mappedRequisitions } = useMemo(() => {
    if (!jobRequisitions) {
      return { filteredRequisitions: [], mappedReqs: [] };
    }

    const mappedRequisitions = jobRequisitions.map((req) => ({
      ...req,

      // 1. Job Title Logic
      jobTitle: req.isForCompany
        ? jobs?.find((j) => j.jobId === req.jobId)?.jobName || "" // Internal: Lookup by ID
        : req.jobTitle || "", // External: Use stored text

      // 2. Department Name Logic
      departmentName: req.isForCompany
        ? departments?.find((d) => d.departmentId === req.departmentId)?.departmentName || ""
        : req.departmentName || "",

      // 3. Branch Name Logic
      branchName: req.isForCompany
        ? branches?.find((b) => b.branchId === req.branchId)?.branchName || ""
        : req.branchName || "",

      // 4. Requested By (Remains the same - always looks up Employee ID)
      requestedByName:
        req.requestedBy === null
          ? "HR"
          : allEmps?.find((e) => e.id === req.requestedBy)?.name || "Unknown",
    }));

    const filteredRequisitions = mappedRequisitions
      .filter((req) =>
        selectedStatus === "all"
          ? req.status !== "COMPLETED" && req.status !== "REJECTED"
          : req.status === selectedStatus
      )
      .filter((req) => {
        const term = searchTerm.toLowerCase();
        return (
          req.jobTitle?.toLowerCase().includes(term) ||
          req.departmentName?.toLowerCase().includes(term) ||
          req.branchName?.toLowerCase().includes(term)
        );
      })
      .sort((a, b) => b.id - a.id);

    return {
      filteredRequisitions,
      mappedRequisitions,
    };
  }, [jobRequisitions, selectedStatus, searchTerm, jobs, departments, branches, allEmps]);

  const handleDetails = (id) => {
    navigate("/recruiting/details", {
      state: {
        requisitionId: id,
        jobRequisitions: mappedRequisitions,
        departments,
        branches,
        canEdit,
        adminOrCeo: adminOrCeo,
      },
    });
  };

  const isAdminOrCeo = employee.isAdmin || employee.role === 1;
  const isDirector = isTopManagement;

  if (loading) return <LoadingScreen />;

  return (
    <MDBox>
      {/* ✅ PAGE HEADER */}
      <Grid container alignItems="center" justifyContent="space-between" pb={2}>
        <Grid item>
          <h2>{t("admin.talentAcquisition.title")}</h2>
        </Grid>
      </Grid>

      {/* ✅ FILTERS + SEARCH */}
      <Grid container spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder={t("admin.talentAcquisition.searchByFilters")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          {/* Make this item take up 4 columns on medium screens */}
          <MDButton
            variant="contained"
            onClick={handleOpenMeetingsDialog}
            color="primary"
            startIcon={<Event />}
          >
            {t("admin.talentAcquisition.viewMeetings")}
          </MDButton>
        </Grid>
      </Grid>

      {/* ✅ SUMMARY CARDS */}
      <Grid container spacing={2} mb={3}>
        {[
          {
            label: t("admin.talentAcquisition.summaryCards.pendingDirector"),
            value: pendingDirectorCount,
            color: "orange",
            icon: "assignment",
            status: "PENDING_DIRECTOR_APPROVAL",
          },
          {
            label: t("admin.talentAcquisition.summaryCards.pendingHr"),
            value: pendingHrCount,
            color: "warning.main",
            icon: "watch_later",
            status: "PENDING_HR_APPROVAL",
          },
          {
            label: t("admin.talentAcquisition.summaryCards.pendingCeo"),
            value: pendingCeoCount,
            color: "purple",
            icon: "person",
            status: "PENDING_CEO_APPROVAL",
          },
          {
            label: t("admin.talentAcquisition.summaryCards.approved"),
            value: approvedCount,
            color: "success.main",
            icon: "check_circle",
            status: "APPROVED",
          },
          {
            label: t("admin.talentAcquisition.summaryCards.completed"), // Make sure this key exists in your translation file
            value: completedCount,
            color: "info.main", // Or use "primary.main" or a specific color like "blue"
            icon: "done_all", // "done_all" is a good icon for completed
            status: "COMPLETED",
          },
          {
            label: t("admin.talentAcquisition.summaryCards.rejected"),
            value: rejectedCount,
            color: "error.main",
            icon: "cancel",
            status: "REJECTED",
          },
        ].map((card) => (
          <Grid item xs={12} sm={4} md={2} key={card.label}>
            <Card
              sx={{
                cursor: "pointer",
                borderRadius: 3,
                boxShadow: selectedStatus === card.status ? 8 : 1,
                border:
                  selectedStatus === card.status ? "2px solid #1976d2" : "2px solid transparent",
                transform: selectedStatus === card.status ? "scale(1.03)" : "scale(1)",
                transition: "all 0.2s ease-in-out",
                "&:hover": { boxShadow: 6 },
              }}
              onClick={() =>
                selectedStatus === card.status
                  ? setSelectedStatus("all")
                  : setSelectedStatus(card.status)
              }
            >
              <CardContent sx={{ textAlign: "center" }}>
                <Icon sx={{ fontSize: 36, color: card.color }}>{card.icon}</Icon>
                <Typography variant="h6">{card.value ?? 0}</Typography>
                <Tooltip title={card.label}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "150px",
                      margin: "0 auto",
                    }}
                  >
                    {card.label.length > 30 ? `${card.label.slice(0, 30)}…` : card.label}
                  </Typography>
                </Tooltip>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ✅ MAIN TABLE (DataGrid) */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <DataGrid
            rows={filteredRequisitions}
            columns={[
              { field: "id", headerName: "#", width: 60 },
              {
                field: "jobTitle",
                headerName: t("admin.talentAcquisition.table.jobTitle"),
                flex: 1,
              },
              ...(isAdminOrCeo
                ? [
                    {
                      field: "departmentName",
                      headerName: t("admin.talentAcquisition.table.department"),
                      flex: 1,
                    },
                  ]
                : []),

              {
                field: "branchName",
                headerName: t("admin.talentAcquisition.table.branch"),
                flex: 1,
              },

              {
                field: "requestedByName",
                headerName: t("admin.talentAcquisition.table.requestedBy"),
                flex: 1,
              },

              {
                field: "numberOfPositions",
                headerName: t("admin.talentAcquisition.table.numberOfPositions"),
                width: 120,
              },
              {
                field: "status",
                headerName: t("admin.talentAcquisition.table.status"),
                width: 180,
                renderCell: (params) => {
                  const status = params.value;
                  const colorMap = {
                    PENDING_HR_APPROVAL: "warning",
                    PENDING_DIRECTOR_APPROVAL: "orange",
                    PENDING_CEO_APPROVAL: "purple",
                    APPROVED: "success",
                    REJECTED: "error",
                  };
                  const labelMap = {
                    PENDING_HR_APPROVAL: t(
                      "admin.talentAcquisition.statusLabels.PENDING_HR_APPROVAL"
                    ),
                    PENDING_DIRECTOR_APPROVAL: t(
                      "admin.talentAcquisition.statusLabels.PENDING_DIRECTOR_APPROVAL"
                    ),
                    PENDING_CEO_APPROVAL: t(
                      "admin.talentAcquisition.statusLabels.PENDING_CEO_APPROVAL"
                    ),
                    APPROVED: t("admin.talentAcquisition.statusLabels.APPROVED"),
                    REJECTED: t("admin.talentAcquisition.statusLabels.REJECTED"),
                    COMPLETED: t("admin.talentAcquisition.statusLabels.COMPLETED"),
                  };
                  return (
                    <Chip
                      label={labelMap[status] || status}
                      color={
                        status === "APPROVED"
                          ? "success"
                          : status === "REJECTED"
                            ? "error"
                            : status === "COMPLETED"
                              ? "info"
                              : "default"
                      }
                      sx={{
                        backgroundColor:
                          status === "PENDING_HR_APPROVAL"
                            ? "#ffb300"
                            : status === "PENDING_DIRECTOR_APPROVAL"
                              ? "#ff9800"
                              : status === "PENDING_CEO_APPROVAL"
                                ? "#9c27b0"
                                : undefined,
                        color: "white",
                      }}
                    />
                  );
                },
              },

              {
                field: "actions",
                headerName: t("admin.talentAcquisition.table.actions"),
                width: 220,
                renderCell: (params) => {
                  // 1. Define logic for visibility
                  const showApprove =
                    // 🔹 PENDING_HR_APPROVAL is now ONLY for Admin
                    (employee.isAdmin && params.row.status === "PENDING_HR_APPROVAL") ||
                    (isTopManagement && params.row.status === "PENDING_DIRECTOR_APPROVAL") ||
                    // 🔹 Keep the old role==1 condition exactly as is
                    (employee.role == 1 &&
                      params.row.status !== "APPROVED" &&
                      params.row.status !== "COMPLETED");

                  const showDelete =
                    (params.row.requestedBy === employee.id || employee.role === 1) &&
                    params.row.status !== "COMPLETED" &&
                    params.row.status !== "REJECTED";

                  return (
                    <Stack direction="row" spacing={1}>
                      {/* 1. View Details (Always Visible) */}
                      <Tooltip title={t("admin.talentAcquisition.tooltips.viewDetails")}>
                        <IconButton color="primary" onClick={() => handleDetails(params.row.id)}>
                          <Visibility />
                        </IconButton>
                      </Tooltip>

                      {/* 2. Approve (Occupies space even if hidden) */}
                      <Tooltip
                        title={showApprove ? t("admin.talentAcquisition.tooltips.approve") : ""}
                      >
                        {/* We wrap in a span so the tooltip doesn't complain, 
              and we control visibility via the IconButton's sx prop */}
                        <span>
                          <IconButton
                            color="success"
                            disabled={!canEdit}
                            onClick={() => handleApprove(params.row)}
                            sx={{ visibility: showApprove ? "visible" : "hidden" }}
                          >
                            <CheckCircle />
                          </IconButton>
                        </span>
                      </Tooltip>

                      {/* 3. Comment (Always Visible per your code) */}
                      {(isAdminOrCeo || isDirector) && (
                        <Tooltip title={t("admin.talentAcquisition.tooltips.addComment")}>
                          <IconButton
                            color="secondary"
                            disabled={!canEdit}
                            onClick={() => {
                              setOpenCommentDialog(true);
                              setCommentData(params.row);
                              setComment(
                                employee.role === 1 ? params.row.ceoComment : params.row.hrComment
                              );
                            }}
                          >
                            <Comment />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* 4. Delete (Occupies space even if hidden) */}
                      <Tooltip
                        title={showDelete ? t("admin.talentAcquisition.tooltips.delete") : ""}
                      >
                        <span>
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteRequest(params.row)}
                            disabled={!canEdit}
                            sx={{ visibility: showDelete ? "visible" : "hidden" }}
                          >
                            <Delete />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  );
                },
              },
            ]}
            getRowId={(row) => row.id}
            autoHeight
            disableSelectionOnClick
            pageSize={10}
            rowsPerPageOptions={[10, 25]}
          />
        </CardContent>
      </Card>
      {canEdit && (
        <MDBox sx={{ position: "fixed", bottom: 20, right: 100 }}>
          <Fab color="primary" aria-label="add" onClick={() => handleOpenDialog(null)}>
            <Icon>add</Icon>
          </Fab>
        </MDBox>
      )}
      <AddTalentRequisitionDialog
        open={openDialog}
        onClose={handleCloseDialog}
        request={selectedRequest}
        branches={branches}
        departments={departments}
        jobs={jobs}
        handleSave={handleSaveRequest}
        handleDelete={handleDeleteRequest}
      />

      <MeetingsDialogWrapper
        openMeetingsDialog={openMeetingsDialog}
        handleCloseMeetingsDialog={handleCloseMeetingsDialog}
        adminOrCeo={adminOrCeo}
        employee={employee}
      />

      {/* Comment Dialog */}
      {(employee.role === 1 || employee.isAdmin === true || isTopManagement === true) && (
        <Dialog
          open={openCommentDialog}
          onClose={() => setOpenCommentDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t("admin.talentAcquisition.dialogs.addCommentTitle")}</DialogTitle>
          <DialogContent>
            <TextField
              label={t("admin.talentAcquisition.comment")}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setOpenCommentDialog(false)} color="secondary">
              {t("common.cancel")}
            </Button>
            <Button onClick={handleCommentSubmit} color="primary">
              {t("common.save")}
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <ConfirmationDialog
        open={confirmOpen}
        onClose={() => !deleteLoading && setConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
        confirmDisabled={deleteLoading}
        loading={deleteLoading}
      />
    </MDBox>
  );
};

export default TalentAcquisitionPage;
